import React, { useEffect, useMemo, useState } from 'react';
import {
    TextField,
    FormControl,
    makeStyles,
    Box,
    Grid,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { CountryAdornment } from './CountrySelect/CountryAdornment';
import { CountryMenu } from './CountrySelect/CountryMenu';
import useDebounce from '@klp/utils/useDebounce';
import type { COUNTRY_MENU_OPTION } from '@klp/leverandor/index';
import {
    COUNTRIES_WITH_API_SUPPORT_LIST,
    useGetLandKodeverkQuery,
} from '@klp/leverandor/index';

const useStyles = makeStyles((theme) => ({
    formField: {
        '& .MuiTypography-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    rowContainer: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}));

export type ManualOrganizationEntryProps = {
    onChange: (orgnummer: string | undefined) => void;
    onCountryChange: (country: string, soCountryId: string | undefined) => void;
    onNameChange: (name: string | undefined) => void;
};

export const ManualOrganizationEntry: React.FC<
    ManualOrganizationEntryProps
> = ({ onChange, onCountryChange, onNameChange }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [orgnummer, setOrgnummer] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [orgnummerError, setOrgnummerError] = useState<string>();
    const [countryMenuAnchor, setCountryMenuAnchor] =
        useState<null | HTMLElement>(null);

    const { data } = useGetLandKodeverkQuery({ locale: 'en' });
    const { debouncedValue: debouncedOrgnummer } = useDebounce(orgnummer, 2000);

    useEffect(() => {
        if (debouncedOrgnummer === '' || !/^\d+$/.test(debouncedOrgnummer)) {
            return;
        }

        onChange(debouncedOrgnummer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedOrgnummer]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newName = event.target.value;
        setName(newName);
        onNameChange(newName || undefined);
    };

    const handleOrgnummerChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setOrgnummerError(undefined);
        if (event.target.value !== '' && !/^\d+$/.test(event.target.value)) {
            setOrgnummerError(
                t('leverandor.createLeverandor.validation.orgnummerDigits'),
            );
        }

        setOrgnummer(event.target.value);
    };

    const handleCountrySelect = (selectedCountry: COUNTRY_MENU_OPTION) => {
        const soCountryId = data
            ? Object.entries(data).find(
                  ([_, value]) => value === selectedCountry,
              )?.[0]
            : undefined;
        onCountryChange(selectedCountry, soCountryId);
    };

    const countryOptions = useMemo(() => {
        return data
            ? Object.entries(data)
                  // Filter out countries that have API support, since they should be selected through the country select with API support
                  .filter(
                      ([_, value]) =>
                          !COUNTRIES_WITH_API_SUPPORT_LIST.includes(value),
                  )
                  .map(([key, value]) => ({ id: key, name: value }))
            : [];
    }, [data]);

    return (
        <Box>
            <FormControl fullWidth={true}>
                <TextField
                    className={classes.formField}
                    id="company-name-input"
                    label={t(
                        'leverandor.createLeverandor.manualEntry.nameLabel',
                    )}
                    placeholder={t(
                        'leverandor.createLeverandor.manualEntry.namePlaceholder',
                    )}
                    variant="outlined"
                    required={true}
                    fullWidth={true}
                    value={name}
                    onChange={handleNameChange}
                    InputProps={{
                        startAdornment: (
                            <CountryAdornment
                                country="Other"
                                setCountryMenuAnchor={setCountryMenuAnchor}
                            />
                        ),
                    }}
                />
            </FormControl>

            <Grid container={true} spacing={2} className={classes.rowContainer}>
                <Grid item={true} xs={6}>
                    <TextField
                        className={classes.formField}
                        id="orgnummer-manual-input"
                        label={t(
                            'leverandor.createLeverandor.manualEntry.orgNumberLabel',
                        )}
                        placeholder={t(
                            'leverandor.createLeverandor.manualEntry.orgNumberPlaceholder',
                        )}
                        variant="outlined"
                        required={true}
                        fullWidth={true}
                        value={orgnummer}
                        onChange={handleOrgnummerChange}
                        inputProps={{
                            'data-testid': 'orgnummer-manual-input',
                        }}
                        error={!!orgnummerError}
                        helperText={orgnummerError}
                    />
                </Grid>
                <Grid item={true} xs={6}>
                    <Autocomplete
                        options={countryOptions}
                        getOptionLabel={(option) => option.name}
                        getOptionSelected={(option, value) =>
                            option.id === value.id
                        }
                        onChange={(_, newValue) => {
                            onCountryChange(
                                newValue?.name ?? 'Other',
                                newValue?.id,
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                className={classes.formField}
                                id="country-text-input"
                                label={t(
                                    'leverandor.createLeverandor.manualEntry.countryLabel',
                                )}
                                placeholder={t(
                                    'leverandor.createLeverandor.manualEntry.countryPlaceholder',
                                )}
                                variant="outlined"
                                required={true}
                                fullWidth={true}
                                inputProps={{
                                    ...params.inputProps,
                                    'data-testid': 'country-text-input',
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>

            <CountryMenu
                country="Other"
                countryMenuAnchor={countryMenuAnchor}
                setCountryMenuAnchor={setCountryMenuAnchor}
                handleCountrySelect={handleCountrySelect}
            />
        </Box>
    );
};
